import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { portalApi } from '../services/api'
import { useLanguage } from '../context/LanguageContext'

// Fix for Leaflet's default marker icons not loading with Vite bundling
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
})

/**
 * MapPicker — OpenStreetMap + Leaflet map with type-ahead autocomplete.
 *
 * User types into the search input, we debounce their keystrokes, and
 * show up to 8 matching address suggestions in a dropdown. Selecting one
 * drops the pin at that location. They can also drag the pin, click the
 * map, or change the country filter to refine results.
 *
 * Props:
 *   lat, lng: current selected coordinates (null if none)
 *   onChange: ({ lat, lng, address?, city?, state? }) — called when the pin moves
 *             (address = full display name string; city/state = structured locality)
 *   searchQuery: optional string — pre-fills the search input on mount
 *   defaultCountryCode: ISO country code (default "ca")
 */
export default function MapPicker({ lat, lng, onChange, searchQuery, height = 380, defaultCountryCode = 'ca', radiusKm = null, markers = null, onMarkerClick = null, highlightFilter = '' }) {
  const { t } = useLanguage()
  const mapContainerRef = useRef(null)
  const mapRef = useRef(null)
  const markerRef = useRef(null)
  const radiusCircleRef = useRef(null)
  const courseMarkersRef = useRef([])
  const searchInputRef = useRef(null)
  const debounceTimerRef = useRef(null)
  const lastQueryRef = useRef('')

  const [searchText, setSearchText] = useState(searchQuery || '')
  const [countryFilter, setCountryFilter] = useState(defaultCountryCode)
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [fetching, setFetching] = useState(false)
  const [resolvedAddress, setResolvedAddress] = useState('')

  // Initialize map once
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    const initLat = lat || 56.1304
    const initLng = lng || -106.3468
    const initZoom = (lat && lng) ? 15 : 4

    const map = L.map(mapContainerRef.current, {
      center: [initLat, initLng],
      zoom: initZoom,
      zoomControl: true,
      scrollWheelZoom: true,
    })

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map)

    if (lat && lng) {
      const marker = L.marker([lat, lng], { draggable: true }).addTo(map)
      marker.on('dragend', handleMarkerDrag)
      markerRef.current = marker
    }

    map.on('click', (e) => {
      // Drop the pin where the user clicked WITHOUT changing zoom or
      // pan — the customer is already looking at where they want.
      placeMarker(e.latlng.lat, e.latlng.lng, false)
    })

    mapRef.current = map

    // If an initial search query was passed, seed the autocomplete with it
    if (searchQuery && !lat && !lng) {
      // Trigger a debounced search immediately
      fetchSuggestions(searchQuery, defaultCountryCode, true)
    }

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
        markerRef.current = null
      }
    }
  }, [])

  // Update marker when lat/lng props change externally
  useEffect(() => {
    if (!mapRef.current || !lat || !lng) return
    placeMarker(lat, lng, false)
  }, [lat, lng])

  // Sync the search input with the parent's searchQuery prop. Used when the
  // parent has an address input (e.g. CourseIntegrations 'Set location' dialog
  // City field) that should drive the map search. The debounced effect on
  // searchText below picks it up automatically and re-geocodes.
  useEffect(() => {
    if (searchQuery && searchQuery !== searchText) {
      setSearchText(searchQuery)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])

  // Draw / update the radius circle whenever lat/lng/radiusKm change.
  // Refit map zoom in two cases:
  //   (a) initial mount with valid coords (deferred via rAF + an
  //       invalidateSize call so Leaflet measures against the
  //       container's *settled* height, not 0).
  //   (b) radius slider changes — show the new circle fully.
  // Don't refit on plain lat/lng change (user clicked map to move pin).
  const didInitialFitRef = useRef(false)
  const lastRadiusRef = useRef(null)
  useEffect(() => {
    if (!mapRef.current) return
    if (radiusCircleRef.current) {
      radiusCircleRef.current.remove()
      radiusCircleRef.current = null
    }
    if (!lat || !lng || !radiusKm || Number(radiusKm) <= 0) return
    const radiusM = Number(radiusKm) * 1000
    const circle = L.circle([lat, lng], {
      radius: radiusM,
      color: '#00d19f',
      weight: 2,
      fillColor: '#00d19f',
      fillOpacity: 0.10,
    }).addTo(mapRef.current)
    radiusCircleRef.current = circle

    const radiusChanged = lastRadiusRef.current !== null && lastRadiusRef.current !== Number(radiusKm)
    const shouldFit = !didInitialFitRef.current || radiusChanged
    lastRadiusRef.current = Number(radiusKm)

    if (shouldFit) {
      // Defer one frame so the map container has its final height
      // before we measure. Without this, fitBounds runs against a
      // 0-height container on first mount and the circle gets cut off.
      requestAnimationFrame(() => {
        if (!mapRef.current || !radiusCircleRef.current) return
        try {
          mapRef.current.invalidateSize()
          mapRef.current.fitBounds(radiusCircleRef.current.getBounds(), { padding: [30, 30], maxZoom: 11 })
        } catch { /* getBounds can throw on tiny radii at edge cases */ }
      })
      didInitialFitRef.current = true
    }
  }, [lat, lng, radiusKm])

  // Optional course pins. `markers` is an array of:
  //   { lat, lng, label, connected?: bool, ...meta }
  // - connected=true → green pin
  // - connected=false → red pin
  // - undefined → yellow (legacy "no semantics" mode)
  //
  // If `onMarkerClick` is provided, clicking a pin invokes it with the
  // full marker object so the caller can show a popup / fire an action.
  // Re-rendered whenever the list changes (typical < 200 entries — fine).
  useEffect(() => {
    if (!mapRef.current) return
    for (const m of courseMarkersRef.current) m.remove()
    courseMarkersRef.current = []
    if (!Array.isArray(markers) || markers.length === 0) return

    // Cluster markers that share the same coords (Carlisle North/
    // South, Lionhead Legends/Masters, 3 Royal Niagara courses at
    // the same point, etc.). One pin per cluster. If N>1 we render
    // a divIcon showing the count; clicking emits the FULL group so
    // the caller can show a popup listing all courses at that point.
    // Color reflects mix:
    //   - all green → green
    //   - all red   → red
    //   - mixed     → green-with-red-dot indicator
    const clusters = new Map()
    for (const m of markers) {
      const mlat = Number(m.lat), mlng = Number(m.lng)
      if (!Number.isFinite(mlat) || !Number.isFinite(mlng)) continue
      const key = `${mlat.toFixed(5)}:${mlng.toFixed(5)}`
      if (!clusters.has(key)) clusters.set(key, { lat: mlat, lng: mlng, items: [] })
      clusters.get(key).items.push(m)
    }

    // Course-name highlight filter: caller passes a substring; markers
    // whose course matches get rendered larger with an amber ring so
    // they pop out of the green/red dot soup. We also collect their
    // coords so we can fit-bounds-zoom the map to the matches below.
    const hl = String(highlightFilter || '').trim().toLowerCase()
    const matchedLatLngs = []
    function matches(item) {
      if (!hl) return false
      const name = String(item.name || item.label || '').toLowerCase()
      const city = String(item.city || '').toLowerCase()
      return name.includes(hl) || city.includes(hl)
    }

    for (const { lat: clat, lng: clng, items } of clusters.values()) {
      const n = items.length
      const connectedCount = items.filter(i => i.connected === true).length
      const allConnected = connectedCount === n && items.every(i => i.connected !== undefined)
      const noneConnected = connectedCount === 0 && items.every(i => i.connected === false)
      const mixed = !allConnected && !noneConnected && items.some(i => i.connected !== undefined)
      const clusterMatches = items.some(matches)
      if (clusterMatches) matchedLatLngs.push([clat, clng])

      let cm
      if (n === 1) {
        const fillColor =
          items[0].connected === true  ? '#16a34a' :
          items[0].connected === false ? '#dc2626' :
                                          '#fbbf24'
        cm = L.circleMarker([clat, clng], {
          radius: clusterMatches ? 11 : 7,
          color: clusterMatches ? '#f59e0b' : '#0f172a',
          weight: clusterMatches ? 3 : 1,
          fillColor, fillOpacity: 0.95, interactive: true,
        }).addTo(mapRef.current)
      } else {
        // Cluster pin — divIcon with the count. Background reflects
        // mix; border stays consistent so the count stays readable.
        const bg = allConnected ? '#16a34a' : noneConnected ? '#dc2626' : '#fbbf24'
        const fg = '#fff'
        const borderRing = clusterMatches ? '#f59e0b'
                          : mixed ? '#dc2626'
                          : 'rgba(15,23,42,0.6)'
        const size = clusterMatches ? 30 : 22
        const fontSize = clusterMatches ? 13 : 11
        const html = `<div style="
          width:${size}px;height:${size}px;border-radius:50%;
          background:${bg};color:${fg};
          border:${clusterMatches ? 4 : 2}px solid ${borderRing};
          display:flex;align-items:center;justify-content:center;
          font-weight:700;font-size:${fontSize}px;
          box-shadow:0 1px 3px rgba(0,0,0,0.4);
          font-family:system-ui,sans-serif;
        ">${n}</div>`
        cm = L.marker([clat, clng], {
          icon: L.divIcon({
            className: '', // suppress default styles
            html,
            iconSize:   [size, size],
            iconAnchor: [size / 2, size / 2],
          }),
          interactive: true,
        }).addTo(mapRef.current)
      }

      // Tooltip: single name for n=1; "N courses (X connected)" for clusters.
      if (n === 1) {
        if (items[0].label) cm.bindTooltip(items[0].label, { direction: 'top', offset: [0, -6] })
      } else {
        cm.bindTooltip(`${n} courses · ${connectedCount} connected`, { direction: 'top', offset: [0, -14] })
      }

      if (typeof onMarkerClick === 'function') {
        cm.on('click', (e) => {
          if (e.originalEvent) {
            e.originalEvent.stopPropagation()
            L.DomEvent.stopPropagation(e.originalEvent)
          }
          // Single pin → original behavior (pass the marker object).
          // Cluster pin → pass an array so the caller can show a list.
          onMarkerClick(n === 1 ? items[0] : { cluster: true, items });
        })
      }
      courseMarkersRef.current.push(cm)
    }

    // Fit the map to the highlight matches. With one match we zoom in to
    // city-level so the reseller can see exactly where it is; with multiple
    // we fit a bounding box around them all. No matches → leave the view
    // alone (don't yank the user's pan/zoom on a typo).
    if (mapRef.current && matchedLatLngs.length > 0) {
      if (matchedLatLngs.length === 1) {
        mapRef.current.setView(matchedLatLngs[0], Math.max(mapRef.current.getZoom(), 11))
      } else {
        try {
          mapRef.current.fitBounds(matchedLatLngs, { padding: [40, 40], maxZoom: 12 })
        } catch (_) { /* fitBounds throws on degenerate point set — fine */ }
      }
    }
  }, [markers, onMarkerClick, highlightFilter])

  // Debounced autocomplete fetch when searchText changes
  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    if (!searchText || searchText.trim().length < 3) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    // Don't re-fetch if this exact query already matches
    if (searchText === lastQueryRef.current) return

    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(searchText, countryFilter)
    }, 350)
    return () => { if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current) }
  }, [searchText, countryFilter])

  async function fetchSuggestions(query, country, autoSelectFirst = false) {
    lastQueryRef.current = query
    setFetching(true)
    try {
      const { data } = await portalApi.get('/lookup/autocomplete', {
        params: { q: query, countryCodes: country || undefined, limit: 8 },
      })
      if (Array.isArray(data)) {
        setSuggestions(data)
        setShowSuggestions(data.length > 0)
        setHighlightedIndex(-1)

        // Auto-select first result if requested (only used for initial mount query)
        if (autoSelectFirst && data.length > 0) {
          selectSuggestion(data[0])
        }
      }
    } catch (err) {
      setSuggestions([])
      setShowSuggestions(false)
    } finally {
      setFetching(false)
    }
  }

  function selectSuggestion(s) {
    setSearchText(s.displayName || '')
    lastQueryRef.current = s.displayName || '' // Prevent re-fetch after setting text
    setSuggestions([])
    setShowSuggestions(false)
    placeMarker(s.latitude, s.longitude, true, s)
  }

  function placeMarker(latitude, longitude, panTo, addressFromSelection = null) {
    if (!mapRef.current) return
    if (markerRef.current) {
      markerRef.current.setLatLng([latitude, longitude])
    } else {
      const marker = L.marker([latitude, longitude], { draggable: true }).addTo(mapRef.current)
      marker.on('dragend', handleMarkerDrag)
      markerRef.current = marker
    }
    if (panTo) {
      mapRef.current.setView([latitude, longitude], 16)
    }

    if (addressFromSelection) {
      setResolvedAddress(addressFromSelection.displayName || '')
      // Contract: `address` is always a string (see reverseGeocode below).
      // Passing the raw suggestion object here made consumers render the
      // literal "[object Object]" (e.g. course city on the Add Course review).
      // Also surface the structured `city`/`state` so consumers that want just
      // the locality (not the full display name) can use them.
      onChange({
        lat: latitude, lng: longitude,
        address: addressFromSelection.displayName || null,
        city: addressFromSelection.address?.city || null,
        state: addressFromSelection.address?.state || null,
      })
    } else {
      reverseGeocode(latitude, longitude, true)
    }
  }

  async function handleMarkerDrag(e) {
    const pos = e.target.getLatLng()
    reverseGeocode(pos.lat, pos.lng, true)
  }

  async function reverseGeocode(latitude, longitude, notifyParent = true) {
    try {
      const { data } = await portalApi.get('/lookup/reverse-geocode', {
        params: { lat: latitude, lng: longitude },
      })
      if (data?.displayName) setResolvedAddress(data.displayName)
      if (notifyParent) {
        // Contract: `address` is always a string. Don't pass the raw
        // geocoder object — consumers (e.g. GroupingEdit) put this
        // straight into a text input + DB column, where an object
        // becomes the literal "[object Object]" via implicit toString.
        // `city`/`state` are the structured locality for consumers that want
        // just the city rather than the full display name.
        onChange({
          lat: latitude, lng: longitude,
          address: data?.displayName || null,
          city: data?.address?.city || null,
          state: data?.address?.state || null,
        })
      }
    } catch {
      if (notifyParent) onChange({ lat: latitude, lng: longitude })
    }
  }

  // Keyboard navigation for the suggestions dropdown
  function handleKeyDown(e) {
    if (!showSuggestions || suggestions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex(i => Math.min(i + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const picked = highlightedIndex >= 0 ? suggestions[highlightedIndex] : suggestions[0]
      if (picked) selectSuggestion(picked)
    } else if (e.key === 'Escape') {
      setShowSuggestions(false)
    }
  }

  return (
    <div style={{ border: '1px solid var(--card-border)', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
      {/* Search bar */}
      <div style={{ padding: 12, borderBottom: '1px solid var(--card-border)', background: 'var(--brand-50)', position: 'relative' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <select
            value={countryFilter}
            onChange={e => {
              setCountryFilter(e.target.value)
              // Re-fetch with new country filter if there's an active query
              if (searchText.trim().length >= 3) fetchSuggestions(searchText, e.target.value)
            }}
            style={countryStyle}
            title={t('sp_limit_search_country', 'Limit search to a specific country')}
          >
            <option value="ca">🇨🇦 {t('sp_canada', 'Canada')}</option>
            <option value="us">🇺🇸 {t('sp_united_states', 'United States')}</option>
            <option value="">🌎 {t('sp_any_country', 'Any country')}</option>
          </select>
          <div style={{ flex: 1, position: 'relative' }}>
            <input
              ref={searchInputRef}
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true) }}
              onBlur={() => { setTimeout(() => setShowSuggestions(false), 200) }}
              placeholder={t('sp_map_search_placeholder', 'Start typing an address, course, or city...')}
              style={inputStyle}
              autoComplete="off"
            />
            {fetching && (
              <div style={spinnerStyle}>...</div>
            )}

            {/* Suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div style={dropdownStyle}>
                {suggestions.map((s, i) => (
                  <div
                    key={s.placeId || i}
                    onMouseEnter={() => setHighlightedIndex(i)}
                    onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s) }}
                    style={{
                      padding: '10px 14px',
                      cursor: 'pointer',
                      borderBottom: i < suggestions.length - 1 ? '1px solid #f1f5f9' : 'none',
                      background: i === highlightedIndex ? 'var(--brand-50)' : '#fff',
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--secondary-950)' }}>
                      {s.address?.line1 || s.displayName?.split(',')[0] || t('sp_unknown', 'Unknown')}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.displayName}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
          {t('sp_map_help_blurb', 'Start typing to see suggestions, or click/drag the pin on the map.')}
        </div>
      </div>

      {/* Map container */}
      <div ref={mapContainerRef} style={{ height, width: '100%' }} />

      {/* Resolved address + coordinates */}
      {resolvedAddress && (
        <div style={{ padding: 12, borderTop: '1px solid var(--card-border)', background: '#f8fafc', fontSize: 12, color: 'var(--text-muted)' }}>
          <strong style={{ color: 'var(--secondary-800)' }}>{t('sp_pin_location', 'Pin location:')}</strong> {resolvedAddress}
        </div>
      )}
      {lat && lng && (
        <div style={{ padding: '8px 12px', borderTop: '1px solid var(--card-border)', background: '#fff', fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
          {Number(lat).toFixed(6)}, {Number(lng).toFixed(6)}
        </div>
      )}
    </div>
  )
}

// ─── Inline styles ──────────────────────────────────────────────────────

const countryStyle = {
  padding: '10px 12px',
  border: '1px solid var(--input-border)',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'inherit',
  outline: 'none',
  background: '#fff',
  cursor: 'pointer',
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--input-border)',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
}

const spinnerStyle = {
  position: 'absolute',
  right: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: 12,
  color: 'var(--text-muted)',
  letterSpacing: 2,
  pointerEvents: 'none',
}

const dropdownStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  background: '#fff',
  border: '1px solid var(--card-border)',
  borderRadius: 10,
  boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
  maxHeight: 320,
  overflowY: 'auto',
  zIndex: 1000,
}
